import type { ReactNode } from "react"

import { terminalDialogClassName } from "@/components/terminal/surfaces"
import { DialogClose, DialogContent } from "@/components/ui/dialog"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

// A dialog's close sits outside its top-right corner, on the scrim, as the
// raised square every header control sits on; the dialog itself stays
// whole (Dhanur, 25 Oct 2026). On a phone, where the dialog is a sheet, it
// sits above the sheet's corner instead.
export const terminalDialogCloseClassName =
  "bg-card text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring absolute top-0 -right-11 inline-flex size-8 cursor-pointer items-center justify-center rounded-sm transition-colors duration-(--duration-fast) ease-(--ease-terminal) focus-visible:ring-1 focus-visible:outline-none max-sm:-top-11 max-sm:right-0"

export const CloseMark = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
  </svg>
)

// Inside a Radix dialog: the primitive's own close, placed outside.
const TerminalDialogClose = ({ className }: { className?: string }) => (
  <DialogClose
    aria-label="Close"
    className={cn(terminalDialogCloseClassName, className)}
  >
    <CloseMark />
  </DialogClose>
)

// A Terminal dialog's content: portaled out of the shell, so it and its scrim
// carry the Terminal theme themselves, with the close outside the corner.
export const TerminalDialogContent = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const { theme } = useTerminalTheme()
  return (
    <DialogContent
      data-terminal-theme={theme}
      overlayProps={{
        "data-terminal-theme": theme,
        className: cn(theme === "dark" && "dark", "bg-surface-scrim"),
      }}
      className={cn(
        theme === "dark" && "dark",
        terminalDialogClassName,
        className
      )}
      shouldHideCloseButton
    >
      <TerminalDialogClose />
      {children}
    </DialogContent>
  )
}
